import { API_ROUTES } from '../constants/api'
import type { VendorSessionData } from '../constants/store'
import {
  CATALOG_KIND_STANDARD,
  DEFAULT_VENDOR_PREP_MINUTES,
  VendorStatus,
  type CatalogProduct,
  type LocationFormState,
  type ProductFormState,
  type Vendor,
  type VendorLocation,
  type VendorProduct,
} from '../constants/vendor'
import { api } from './api-client'

type Raw = Record<string, unknown>

function str(value: unknown, fallback = ''): string {
  return value == null ? fallback : String(value)
}

function num(value: unknown, fallback = 0): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function bool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function mapLocation(raw: Raw): VendorLocation {
  return {
    id: str(raw.id),
    vendorId: str(raw.vendorId),
    name: str(raw.name),
    address: raw.address ? str(raw.address) : null,
    latitude: num(raw.latitude),
    longitude: num(raw.longitude),
    supportsExpress: bool(raw.supportsExpress, true),
    isActive: bool(raw.isActive, true),
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
  }
}

function mapVendor(raw: Raw): Vendor {
  const status = str(raw.status, VendorStatus.Pending)
  const locationRaw =
    raw.location && typeof raw.location === 'object' ? (raw.location as Raw) : null
  return {
    id: str(raw.id),
    name: str(raw.name),
    phone: str(raw.phone),
    status:
      status === VendorStatus.Active ||
      status === VendorStatus.Suspended ||
      status === VendorStatus.Pending
        ? status
        : VendorStatus.Pending,
    location: locationRaw ? mapLocation(locationRaw) : null,
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
  }
}

function mapVendorProduct(raw: Raw): VendorProduct {
  return {
    id: str(raw.id),
    vendorLocationId: str(raw.vendorLocationId),
    productId: str(raw.productId),
    prepTimeMinutes: num(raw.prepTimeMinutes, DEFAULT_VENDOR_PREP_MINUTES),
    isActive: bool(raw.isActive, true),
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
  }
}

function mapProduct(raw: Raw): CatalogProduct {
  return {
    id: str(raw.id),
    name: str(raw.name),
    slug: str(raw.slug),
    kind: str(raw.kind),
    status: str(raw.status),
  }
}

function toArray(raw: unknown): Raw[] {
  if (Array.isArray(raw)) return raw as Raw[]
  if (raw && typeof raw === 'object') {
    const data = (raw as { data?: unknown; items?: unknown }).data
    if (Array.isArray(data)) return data as Raw[]
    const items = (raw as { items?: unknown }).items
    if (Array.isArray(items)) return items as Raw[]
  }
  return []
}

function locationFormToPayload(form: LocationFormState) {
  return {
    name: form.name.trim(),
    address: form.address.trim() || null,
    latitude: Number(form.latitude),
    longitude: Number(form.longitude),
    supportsExpress: form.supportsExpress,
    isActive: form.isActive,
  }
}

export async function fetchVendor(vendorId: string): Promise<Vendor> {
  return mapVendor(await api.get<Raw>(API_ROUTES.vendor(vendorId)))
}

export async function updateVendorLocation(
  vendorId: string,
  form: LocationFormState,
): Promise<Vendor> {
  return mapVendor(
    await api.patch<Raw>(API_ROUTES.vendor(vendorId), {
      location: locationFormToPayload(form),
    }),
  )
}

export async function fetchVendorProducts(
  vendorId?: string,
): Promise<VendorProduct[]> {
  const query = vendorId ? `?vendorId=${encodeURIComponent(vendorId)}` : ''
  const rows = await api.get<Raw[]>(`${API_ROUTES.vendorProducts}${query}`)
  return rows.map(mapVendorProduct)
}

export async function createVendorProduct(
  vendorId: string,
  form: ProductFormState,
): Promise<VendorProduct[]> {
  return toArray(
    await api.post<unknown>(API_ROUTES.vendorProducts, {
      vendorId,
      productIds: form.productIds,
      prepTimeMinutes:
        Number(form.prepTimeMinutes) || DEFAULT_VENDOR_PREP_MINUTES,
      isActive: form.isActive,
    }),
  ).map(mapVendorProduct)
}

export async function updateVendorProduct(
  id: string,
  patch: {
    prepTimeMinutes?: number
    isActive?: boolean
  },
): Promise<VendorProduct> {
  return mapVendorProduct(
    await api.patch<Raw>(API_ROUTES.vendorProduct(id), patch),
  )
}

export async function deleteVendorProduct(id: string): Promise<void> {
  await api.del(API_ROUTES.vendorProduct(id))
}

export async function fetchCatalogProducts(): Promise<CatalogProduct[]> {
  const rows = toArray(await api.get<unknown>(API_ROUTES.products))
  return rows
    .map(mapProduct)
    .filter((product) => product.kind === CATALOG_KIND_STANDARD)
}

export function locationToForm(location: VendorLocation): LocationFormState {
  return {
    name: location.name,
    address: location.address ?? '',
    latitude: String(location.latitude),
    longitude: String(location.longitude),
    supportsExpress: location.supportsExpress,
    isActive: location.isActive,
  }
}

export async function loadVendorData(
  vendorId: string,
  options?: { includeCatalog?: boolean },
): Promise<VendorSessionData> {
  const includeCatalog = options?.includeCatalog ?? false
  const [vendor, products, catalog] = await Promise.all([
    fetchVendor(vendorId),
    fetchVendorProducts(vendorId),
    includeCatalog ? fetchCatalogProducts() : Promise.resolve(null),
  ])
  return {
    vendorId,
    vendor,
    products,
    catalog,
  }
}
